import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HTTP_STATUS } from '../../../constants/httpStatus';
import { useGuestSession } from '../../../contexts/GuestSessionContext';
import { useOverlay } from '../../../contexts/OverlayContext';
import { useTOS } from '../../../contexts/SettingsContext/hooks/useTOS';
import { useFetchToken, useScheduleInfo, useToastHelpers, useUser } from '../../../hooks';
import { useMeetingLink } from '../../../hooks/useMeetingLink';
import { authRedirectService } from '../../../services/authRedirectService';
import { meetingSessionService } from '../../../services/meetingSessionService';
import type { BaseOverlayProps } from '../../../types';
import { devLog } from '../../../utils/devLog';
import type { JitsiErrorPayload } from '../../../utils/jitsiAPIUtils';
import { retrieveJitsiMeetId } from '../../../utils/jitsiAPIUtils';
import { isDev } from '../../../utils/optimizedImports';
import type { JitsiError } from '../../JitsiErrorBanner/JitsiErrorBanner';
import { JitsiErrorBanner } from '../../JitsiErrorBanner/JitsiErrorBanner';
import { LoadingPage } from '../../LoadingPage';
import { LobbyMessageBanner } from '../../LobbyMessageBanner';
import { MeetingErrorPage } from '../../MeetingErrorPage';
import { TOSModal } from '../../TOSModal';
import { ToastContainer } from '../../Toast';
import { TranscriptPanel } from '../../TranscriptPanel';
import { WaitingForHostPage } from '../../WaitingForHostPage';
import { OverlayContainer } from '../OverlayContainer';
import { useJitsiDeviceSync } from './hooks/useJitsiDeviceSync';
import { useJitsiMeeting } from './hooks/useJitsiMeeting';
import { useMeetingSessionToasts } from './hooks/useMeetingSessionToasts';
import { usePasswordProtection } from './hooks/usePasswordProtection';

declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: JitsiMeetExternalAPI has no TypeScript definitions
    JitsiMeetExternalAPI: any;
  }
}

export interface MeetingOverlaysProps extends Omit<BaseOverlayProps, 'id'> {
  onViewTranscriptClick?: () => void;
}

/**
 * MeetingOverlays Component
 * Phase 4: Enhanced with comprehensive TypeScript interfaces and configuration
 * Centralized component for all meeting overlay elements
 */

export const MeetingOverlays: React.FC<MeetingOverlaysProps> = ({
  onViewTranscriptClick,
  onError,
  'data-testid': dataTestId,
}) => {
  // biome-ignore lint/suspicious/noExplicitAny: JitsiMeetExternalAPI has no TypeScript definitions
  const apiRef = useRef<any>(null);
  const cancelPendingRef = useRef<(() => void) | null>(null);
  const navigate = useNavigate();
  const { guestSession } = useGuestSession();
  const guestName = guestSession?.name ?? null;
  const [isTosModalOpen, setIsTosModalOpen] = useState(false);
  const [isMeetingVisible, setIsMeetingVisible] = useState(false);
  // SM-1165: distinguishes the hang-up teardown loader from the initial join
  // loader so the "Leaving the meeting…" message (not "Setting up…") shows
  // while the meeting is winding down.
  const [isLeavingMeeting, setIsLeavingMeeting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isInLobby, setIsInLobby] = useState(false);
  const [jitsiErrors, setJitsiErrors] = useState<JitsiError[]>([]);
  const { toasts, removeToast, showToast, showError } = useToastHelpers();

  useMeetingSessionToasts({ showToast, removeToast });

  const jitsiDefaultMessages = useMemo<Record<string, (name: string) => string>>(
    () => ({
      CONNECTION: (name) => `Meeting connection lost (${name})`,
      CONFERENCE: (name) => `Conference error occurred (${name})`,
      CONFIG: (name) => `Configuration error (${name})`,
    }),
    [],
  );

  const handleJitsiError = useCallback(
    (payload: JitsiErrorPayload) => {
      if (!payload.name) {
        return;
      }

      const isFatal = payload.isFatal ?? false;

      const buildFallbackMessage = (name: string): string => {
        const defaultMessageFn = jitsiDefaultMessages[payload.type];
        const base = defaultMessageFn ? defaultMessageFn(name) : `Error detected: ${name}`;
        const suffix = isFatal
          ? 'meeting experience will be disrupted'
          : 'meeting experience might be degraded';
        return `${base}: ${suffix}.`;
      };

      const message = payload.message || buildFallbackMessage(payload.name);

      setJitsiErrors((prev) => {
        const isDuplicate = prev.some((e) => e.name === payload.name && e.type === payload.type);
        if (isDuplicate) {
          return prev;
        }
        return [
          ...prev,
          {
            id: `jitsi-err-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
            name: payload.name,
            type: payload.type,
            message,
            isFatal,
            timestamp: Date.now(),
          },
        ];
      });
    },
    [jitsiDefaultMessages],
  );

  useEffect(() => {
    // biome-ignore lint/suspicious/noExplicitAny: dev helper for console testing
    (window as any).testJitsiError = handleJitsiError;
    return () => {
      // biome-ignore lint/suspicious/noExplicitAny: dev helper cleanup
      delete (window as any).testJitsiError;
    };
  }, [handleJitsiError]);

  const dismissJitsiError = useCallback((id: string) => {
    setJitsiErrors((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const clearJitsiErrors = useCallback(() => {
    setJitsiErrors([]);
  }, []);

  const handleLobbyEntered = useCallback(() => {
    setIsInLobby(true);
  }, []);

  const handleConferenceJoined = useCallback(() => {
    setIsInLobby(false);
  }, []);

  const handleMeetingLeaving = useCallback(() => {
    setIsLeavingMeeting(true);
  }, []);

  const { meetingId, password, extraParams } = useMeetingLink();
  const { data: scheduleInfo, isPending: isScheduleInfoPending } = useScheduleInfo(
    meetingId ?? null,
  );

  const { addOverlay, removeOverlay, getOverlay } = useOverlay();
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [showTranscriptButton, setShowTranscriptButton] = useState(false);

  // Get user data from the new UserContext
  const { isAuthenticated, idToken, isLoading: isUserLoading, userName } = useUser();

  // Get TOS acceptance state
  const {
    needsAcceptance,
    guestNeedsAcceptance,
    acceptGuestTos,
    isLoadingTOS: isTosLoading,
    tosAcceptance,
    tosMetadata,
  } = useTOS();

  // Load jitsiMeetId asynchronously
  const [jitsiMeetId, setJitsiMeetId] = useState<string | null>(null);
  const [jitsiMeetIdError, setJitsiMeetIdError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadJitsiMeetId() {
      try {
        const id = await retrieveJitsiMeetId();
        devLog('[overlay] setting jitsi meet id:', id);
        if (isMounted) {
          setJitsiMeetId(id);
        }
      } catch (error) {
        console.error('[overlay] failed to retrieve jitsi meet id:', error);
        if (isMounted) {
          setJitsiMeetIdError(error instanceof Error ? error.message : String(error));
        }
      }
    }

    loadJitsiMeetId();

    return () => {
      isMounted = false;
    };
  }, []);

  // Pin the meeting token to the idToken we joined with: a cross-tab logout clears
  // idToken, and refetching it unauthenticated would tear the live meeting down.
  const isMeetingActive = meetingSessionService.isMeetingActive();
  // SSO restore / token refresh can report isAuthenticated=false for a moment.
  // Fetching a guest JWT in that window makes Jitsi ask for a display name, then
  // dismiss it when the real identity arrives.
  const isAuthSettled = !isUserLoading || isMeetingActive;
  const pinnedIdTokenRef = useRef(idToken);
  if (!isMeetingActive) {
    pinnedIdTokenRef.current = idToken;
  }
  const effectiveIdToken = isMeetingActive ? pinnedIdTokenRef.current : idToken;

  const {
    data: activeToken,
    error: activeTokenError,
    refetch: refetchToken,
    isFetching: isTokenFetching,
  } = useFetchToken(
    effectiveIdToken ?? null,
    jitsiMeetId ?? '',
    meetingId ?? '',
    guestName,
    isAuthSettled,
  );

  const meetingToken = isAuthSettled ? (activeToken ?? null) : null;
  const jitsiDisplayName = guestName || (isAuthenticated ? userName : null);

  // Track the last error status/message so we can persist the UI during refetches
  const lastErrorStatusRef = useRef<number | undefined>(undefined);
  const lastErrorMessageRef = useRef<string | undefined>(undefined);
  if (activeTokenError) {
    lastErrorStatusRef.current = (activeTokenError as Error & { status?: number }).status;
    lastErrorMessageRef.current = activeTokenError.message;
  } else if (activeToken) {
    lastErrorStatusRef.current = undefined;
    lastErrorMessageRef.current = undefined;
  }

  // Memoize onMeetingClose callback to prevent unnecessary re-renders
  const onMeetingClose = useCallback(() => {
    setIsMeetingVisible(false);
  }, []);

  // A successful (re)join always follows a fresh conference, so clear the
  // leaving flag as soon as the meeting becomes visible again.
  useEffect(() => {
    if (isMeetingVisible) {
      setIsLeavingMeeting(false);
    }
  }, [isMeetingVisible]);

  // Custom hooks for meeting management
  const deviceSync = useJitsiDeviceSync();

  const passwordProtection = usePasswordProtection({
    password,
    apiRef,
    onMeetingClose,
  });

  // Monitor transcript panel overlay state to sync with external changes
  useEffect(() => {
    const checkTranscriptOverlay = () => {
      const transcriptOverlay = getOverlay('transcript-panel');
      const overlayExists = !!transcriptOverlay;

      // If local state says it's open but overlay doesn't exist, sync the state
      if (isTranscriptOpen && !overlayExists) {
        devLog('Transcript panel was closed externally, syncing state');
        setIsTranscriptOpen(false);
      }
    };

    // Check immediately and then periodically
    checkTranscriptOverlay();
    const interval = setInterval(checkTranscriptOverlay, 500);

    return () => clearInterval(interval);
  }, [isTranscriptOpen, getOverlay]);

  // Listen for chat state changes from Jitsi
  useEffect(() => {
    const handleChatUpdate = (event: Event) => {
      const chatEvent = event as CustomEvent<{ isOpen: boolean }>;
      if (chatEvent.detail && typeof chatEvent.detail.isOpen === 'boolean') {
        setIsChatOpen(chatEvent.detail.isOpen);
        devLog('Chat state updated:', chatEvent.detail.isOpen);
      }
    };

    // Listen for custom chat events
    window.addEventListener('jitsi-chat-updated', handleChatUpdate);

    // Also try to get the initial chat state from the global Jitsi API if available
    const checkInitialChatState = () => {
      try {
        // @ts-expect-error - accessing global Jitsi API
        const api = window.jitsiApi || window.JitsiMeetExternalAPI;
        if (api && typeof api.isOpen === 'function') {
          const initialChatState = api.isOpen('chat');
          setIsChatOpen(initialChatState);
        }
      } catch (_error) {
        // Ignore errors - chat state will be updated via events
      }
    };

    checkInitialChatState();

    return () => {
      window.removeEventListener('jitsi-chat-updated', handleChatUpdate);
    };
  }, []);

  // Show transcript button with a delay after meeting becomes visible
  useEffect(() => {
    if (isMeetingVisible) {
      const TranscriptButtonDelayMs = 2000; // 2 second delay after meeting is visible
      const timer = setTimeout(() => {
        setShowTranscriptButton(true);
      }, TranscriptButtonDelayMs);

      return () => clearTimeout(timer);
    }
    // Reset when meeting is not visible
    setShowTranscriptButton(false);
  }, [isMeetingVisible]);

  const handleViewTranscriptClick = () => {
    try {
      if (isTranscriptOpen) {
        // Close transcript panel
        setIsTranscriptOpen(false);
        removeOverlay('transcript-panel');
      } else {
        // Open transcript panel
        setIsTranscriptOpen(true);
        addOverlay({
          id: 'transcript-panel',
          component: React.createElement(TranscriptPanel, {
            isOpen: true,
            onClose: () => {
              setIsTranscriptOpen(false);
              removeOverlay('transcript-panel');
            },
            'data-testid': 'transcript-panel-overlay',
          }),
          position: {
            top: '0px',
            left: '0px',
          },
          interactive: true,
          zIndex: 99999,
          priority: 'high',
          category: 'transcript',
          description: 'Meeting transcript panel',
          version: '1.0.0',
          ariaLabel: 'Meeting transcript panel',
          focusable: true,
        });
      }

      if (onViewTranscriptClick) {
        onViewTranscriptClick();
      }
    } catch (error) {
      console.error('Error in view transcript:', error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  };

  // TOS Modal handlers
  const handleTosAccept = () => {
    setIsTosModalOpen(false);
    if (guestSession) {
      acceptGuestTos();
    }
  };

  const handleTosCancel = () => {
    setIsTosModalOpen(false);

    const meetingContainer = document.querySelector('#meeting-container') as HTMLElement;
    if (meetingContainer) {
      meetingContainer.classList.remove('visible');
      meetingContainer.style.display = 'none';
    }

    if (apiRef.current) {
      try {
        apiRef.current.dispose();
      } catch (e) {
        console.warn('[TOS] Error disposing Jitsi API:', e);
      }
      apiRef.current = null;
    }

    document.body.classList.remove('meeting-active');
    setIsMeetingVisible(false);

    // Without an explicit redirect the user falls back into the "Setting up your meeting"
    // loader (`activeToken && !isMeetingVisible`) and gets stuck there with no recovery path.
    authRedirectService.removeMeetingHashParams();
    navigate('/');
  };

  // Debug TOS state
  useEffect(() => {
    if (isDev) {
      devLog('[TOS Debug] State:', {
        isTosLoading,
        tosAcceptance,
        tosMetadata,
        needsAcceptance: needsAcceptance(),
        isMeetingVisible,
        isAuthenticated,
        isTosModalOpen,
      });
    }
  }, [
    isTosLoading,
    tosAcceptance,
    tosMetadata,
    isMeetingVisible,
    isAuthenticated,
    isTosModalOpen,
    needsAcceptance,
  ]);

  useEffect(() => {
    if (!isMeetingVisible || isTosLoading) {
      return;
    }
    const shouldShowTos = guestSession
      ? guestNeedsAcceptance()
      : isAuthenticated && needsAcceptance();
    if (shouldShowTos) {
      setIsTosModalOpen(true);
    }
  }, [
    isMeetingVisible,
    isAuthenticated,
    guestSession,
    needsAcceptance,
    guestNeedsAcceptance,
    isTosLoading,
  ]);

  const removeSessionForMeetingLink = useCallback(() => {
    authRedirectService.removeMeetingHashParams();
  }, []);

  const errorStatus = (activeTokenError as (Error & { status?: number }) | null)?.status;
  const isGenericError =
    activeTokenError &&
    errorStatus !== HTTP_STATUS.locked &&
    errorStatus !== HTTP_STATUS.notAcceptable;

  useEffect(() => {
    if (activeTokenError) {
      devLog('activeTokenError: ', activeTokenError.message);
    }
    if (isGenericError) {
      showError('Meeting Error', activeTokenError.message);
    }
  }, [activeTokenError, isGenericError, showError]);

  // Cleanup when component unmounts (e.g., browser back button or route change)
  useEffect(() => {
    return () => {
      document.body.classList.remove('meeting-active');

      const meetingContainer = document.querySelector('#meeting-container') as HTMLElement;
      if (meetingContainer) {
        meetingContainer.classList.remove('visible');
        meetingContainer.style.display = 'none';
      }

      setIsMeetingVisible(false);
      setIsChatOpen(false);
      setIsInLobby(false);
      removeOverlay('transcript-panel');
    };
  }, [removeOverlay]);

  // Initialize Jitsi meeting with all hooks
  useJitsiMeeting({
    activeToken: meetingToken,
    meetingId: meetingId ?? null,
    meetingDisplayName: scheduleInfo?.display_name ?? null,
    isMeetingDisplayNamePending: isScheduleInfoPending,
    isAuthenticated,
    apiRef,
    setIsMeetingVisible,
    setIsChatOpen,
    removeOverlay,
    onJitsiError: handleJitsiError,
    clearJitsiErrors,
    onConferenceJoined: handleConferenceJoined,
    onLobbyEntered: handleLobbyEntered,
    onMeetingLeaving: handleMeetingLeaving,
    extraParams,
    startDeviceSync: deviceSync.startDeviceSync,
    cleanup: deviceSync.cleanup,
    setupPasswordListener: passwordProtection.setupPasswordListener,
    cancelPendingRef,
    guestName,
    displayName: jitsiDisplayName,
  });

  function _renderViewTranscript() {
    // Only render after the delay when meeting is active
    if (!showTranscriptButton) {
      return null;
    }

    return (
      <div data-testid={dataTestId} className="meetingOverlays">
        {!isChatOpen && (
          <button
            type="button"
            className="view-transcript-button"
            onClick={handleViewTranscriptClick}
            data-testid="view-transcript-button"
            aria-label={isTranscriptOpen ? 'Close transcript panel' : 'View full transcript'}
          >
            {isTranscriptOpen ? 'Close transcript' : 'View full transcript'}
          </button>
        )}
      </div>
    );
  }

  // Reload, not refetch: _jitsiMeetId is a one-shot module singleton — refetch can't re-run it; only a full reload does.
  if (jitsiMeetIdError) {
    return (
      <MeetingErrorPage
        meetingId={meetingId || ''}
        title="Failed to initialize the application."
        description="Please check your network connection and firewall settings."
        errorDetail={jitsiMeetIdError}
        onRetry={() => window.location.reload()}
        onBackToHome={() => {
          removeSessionForMeetingLink();
          navigate('/');
        }}
      />
    );
  }

  // If we have a token (authenticated or guest), keep showing a loader until Jitsi is ready, then render overlays
  if (meetingToken) {
    if (!isMeetingVisible) {
      return (
        <div className="loading-overlay">
          <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
          <LoadingPage
            room={meetingId || ''}
            message={isLeavingMeeting ? 'Leaving the meeting…' : undefined}
          />
        </div>
      );
    }
    return (
      <>
        {isInLobby && <LobbyMessageBanner message={scheduleInfo?.lobby_msg} />}
        <JitsiErrorBanner errors={jitsiErrors} onDismiss={dismissJitsiError} />
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
        <OverlayContainer />
        {/* TOS Modal — for authenticated users (server-persisted) and guests
            (acceptance kept per-tab in sessionStorage, not on the backend). */}
        {(isAuthenticated || guestSession) && (
          <TOSModal
            isOpen={isTosModalOpen}
            onClose={handleTosCancel}
            onAccept={handleTosAccept}
            persistToBackend={!guestSession}
          />
        )}
      </>
    );
  }

  if (activeTokenError) {
    const errorStatus = (activeTokenError as Error & { status?: number }).status;

    if (errorStatus === HTTP_STATUS.locked) {
      return (
        <WaitingForHostPage
          meetingId={meetingId || ''}
          errorDetail={activeTokenError.message}
          onRetry={() => {
            refetchToken();
          }}
          onBackToHome={() => {
            removeSessionForMeetingLink();
            navigate('/');
          }}
        />
      );
    }

    if (errorStatus === HTTP_STATUS.notAcceptable) {
      return (
        <MeetingErrorPage
          meetingId={meetingId || ''}
          errorDetail={activeTokenError.message}
          onRetry={() => {
            refetchToken();
          }}
          onBackToHome={() => {
            removeSessionForMeetingLink();
            navigate('/');
          }}
        />
      );
    }

    return (
      <div className="loading-overlay">
        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
        <LoadingPage room={meetingId || ''} />
      </div>
    );
  }

  // While refetching after a 423, keep showing WaitingForHostPage to prevent background flash
  if (isTokenFetching && lastErrorStatusRef.current === HTTP_STATUS.locked) {
    return (
      <WaitingForHostPage
        meetingId={meetingId || ''}
        errorDetail={lastErrorMessageRef.current}
        onRetry={() => {
          refetchToken();
        }}
        onBackToHome={() => {
          removeSessionForMeetingLink();
          navigate('/');
        }}
      />
    );
  }

  return (
    <div className="loading-overlay">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      <LoadingPage room={meetingId || ''} />
    </div>
  );
};
